import clsx from "clsx";
import { X } from "lucide-react";
import { IconButton } from "../IconButton/IconButton.js";
import styles from "./Tag.module.scss";

/** Semantic fill. `neutral` is the unclaimed default — a plain paper tag with a hairline; `muted` is the
 * quieter sunken fill for tags that are content rather than status. */
export type TagTone = "accent" | "neutral" | "muted" | "success" | "warning" | "danger";

/** `sm` is the chip rhythm (genres, for-fans-of). `md` matches `--ac-control-h`, for tags that sit in a
 * form's vertical rhythm beside inputs — see the comparable-artist pills. */
export type TagSize = "sm" | "md";

export interface TagProps {
  /** The tag's label. Keep it a plain string where possible — it doubles as the remove button's name. */
  children: React.ReactNode;
  tone?: TagTone;
  size?: TagSize;
  /** Secondary text rendered lighter INSIDE the tag, after the label (e.g. "770.5K listeners"). */
  meta?: React.ReactNode;
  /** Supply this to render the × affordance. Omit it for a read-only tag. */
  onRemove?: () => void;
  /** Names the remove button ("Remove {…}"). Defaults to `children` when that is a plain string; pass it
   * explicitly for rich children, or the button ends up with a useless name. */
  removeLabel?: string;
  title?: string;
  className?: string;
}

/**
 * A tag: a short label in a pill, optionally removable.
 *
 * **Why this is a primitive.** The remove affordance is the reason. Six hand-rolled versions of it existed and
 * every one had the same defect: a `<button>` carries a UA `padding: 1px 6px`, which shrinks the content box
 * below the icon, so the glyph overflows rightward and sits ~5px off-centre inside a still-perfectly-round
 * box (measured 2026-08-18). Owning the geometry here means it cannot be written a seventh time — the same
 * argument that put the overlays in this package.
 *
 * **What this is NOT.** It is not the read-only status pill (mono, uppercase, driven by a state enum — see
 * `StatusPill`), nor a filter/toggle chip (it has a pressed state and behaves like a control), nor an
 * identity chip (avatar + name + sub). Those are separate components that happen to be round; folding them
 * in here produces one component with a dozen variants, which is worse than the duplication.
 *
 * **Type note:** this renders Inter, deliberately. STYLE-GUIDE ruling 8 currently puts "chips" on the mono
 * side and that boundary is an OPEN DELTA under re-examination — the shipped accent tags are Inter, so this
 * follows the code rather than pre-empting a ruling nobody has made. The mono label-chips
 * (`SupportFinderModal`, `FriendProfileModal`) are deliberately NOT adopted until ruling 8 resolves.
 */
export function Tag({
  children,
  tone = "neutral",
  size = "sm",
  meta,
  onRemove,
  removeLabel,
  title,
  className,
}: TagProps) {
  const name = removeLabel ?? (typeof children === "string" ? children : undefined);

  return (
    <span
      className={clsx(styles.tag, className)}
      data-tone={tone}
      data-size={size}
      data-removable={onRemove ? "" : undefined}
      title={title}
    >
      <span className={styles.label}>{children}</span>
      {meta !== undefined && meta !== null && <span className={styles.meta}>{meta}</span>}
      {onRemove && (
        <IconButton
          icon={X}
          size="xs"
          label={name ? `Remove ${name}` : "Remove"}
          onClick={onRemove}
          className={styles.remove}
        />
      )}
    </span>
  );
}
